import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import enUS from 'i18n/locales/en-US'
import esMX from 'i18n/locales/es-MX'

/**
 * Minimal i18n: two dictionaries, `t(key, params)` with `{name}` interpolation
 * and `_one` / `_other` plurals (driven by `params.count`), plus locale-aware
 * formatters. The chosen locale is kept in localStorage and sent to the API as
 * Accept-Language (see services/request.service.js), which localizes its errors.
 */

export const LOCALES = [
  { code: 'es-MX', short: 'ES', label: 'Español (México)' },
  { code: 'en-US', short: 'EN', label: 'English (US)' }
]
const DICTIONARIES = { 'en-US': enUS, 'es-MX': esMX }
const FALLBACK = 'en-US'
const STORAGE_KEY = 'lcost_locale'

export const detectLocale = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (DICTIONARIES[stored]) return stored
  } catch { /* ignore */ }
  const nav = (typeof navigator !== 'undefined' && navigator.language) || ''
  return nav.toLowerCase().startsWith('es') ? 'es-MX' : 'en-US'
}

// Current locale for code outside React (the axios interceptor).
let currentLocale = detectLocale()
export const getLocale = () => currentLocale

const lookup = (dict, key) => key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), dict)

const interpolate = (text, params) =>
  text.replace(/\{(\w+)\}/g, (match, name) => (params[name] === undefined || params[name] === null ? match : String(params[name])))

export const translate = (locale, key, params = {}) => {
  const candidates = params.count !== undefined ? [`${key}_${Number(params.count) === 1 ? 'one' : 'other'}`, key] : [key]
  for (const dict of [DICTIONARIES[locale], DICTIONARIES[FALLBACK]]) {
    for (const k of candidates) {
      const text = lookup(dict, k)
      if (typeof text === 'string') return interpolate(text, params)
    }
  }
  return key
}

const makeFormatters = (locale) => {
  const decimals = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const isMissing = (v) => v === null || v === undefined || v === '' || !Number.isFinite(Number(v))
  return {
    money: (value, currency = '') => (isMissing(value) ? '—' : `${decimals.format(Number(value))}${currency ? ` ${currency}` : ''}`),
    number: (value, maxDigits = 4) => (isMissing(value) ? '—' : new Intl.NumberFormat(locale, { maximumFractionDigits: maxDigits }).format(Number(value))),
    pct: (fraction) => (isMissing(fraction) ? '—' : new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(Number(fraction))),
    day: (value) => {
      if (!value) return '—'
      const dt = DateTime.fromISO(String(value).slice(0, 10))
      return dt.isValid ? dt.setLocale(locale).toLocaleString(DateTime.DATE_MED) : '—'
    },
    date: (value) => {
      if (!value) return '—'
      const dt = typeof value === 'string' ? DateTime.fromISO(value) : DateTime.fromJSDate(value)
      return dt.isValid ? dt.setLocale(locale).toLocaleString(DateTime.DATETIME_MED) : '—'
    }
  }
}

const I18nContext = createContext(null)

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(currentLocale)

  const setLocale = useCallback((next) => {
    if (!DICTIONARIES[next]) return
    currentLocale = next
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
    document.documentElement.lang = next
    setLocaleState(next)
  }, [])

  const value = useMemo(() => {
    document.documentElement.lang = locale
    document.title = `${translate(locale, 'app.name')} · ${translate(locale, 'app.descriptor')}`
    const t = (key, params) => translate(locale, key, params)
    return { locale, setLocale, t, fmt: makeFormatters(locale) }
  }, [locale, setLocale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>')
  return ctx
}

/**
 * Text of an engine warning/error ({ code, params, message }). Results stored
 * before issues had codes are plain strings: shown as they are.
 */
export const formatIssue = (t, issue) => {
  if (typeof issue === 'string') return issue
  const { code, params = {}, message } = issue || {}
  const key = `issues.${code}`
  const p = { ...params }
  if (p.category) p.category = t(`layers.${p.category}`)
  if (p.basis) p.basis = t(`bases.${p.basis}`)
  if (p.kind) p.subject = t(`issues.subject.${p.kind}`, { name: p.name })
  if (p.reason) p.reason = t(`fx.errors.${p.reason}`)
  const text = t(key, p)
  return text === key ? message || code : text
}

/**
 * Where an exchange rate came from: { source: 'manual' | 'pedimento' | 'banxico:SF60653', date }.
 */
export const formatRateSource = (t, fmtDate, meta) => {
  if (!meta || !meta.source) return null
  const [kind, series] = meta.source.split(':')
  const label = t(`fx.sources.${kind}`, { series })
  return meta.date ? `${label} · ${t('fx.asOf', { date: fmtDate(meta.date) })}` : label
}

/**
 * Message for a failed request: the API's (already localized through
 * Accept-Language) or the given fallback.
 */
export const apiError = (error, fallback) => error?.response?.data?.message || fallback
